import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';

import { initializeAnalytics } from './hooks/useAnalytics';
import router from './router';
import { SnackbarStoreProvider } from './store/SnackbarProvider';
import { PaymentStoreProvider } from './store/payments';
import { RecentlyLinkedPolicyProvider } from './store/recentlyLinkedPolicy';
import theme from './styles/theme';
import createEmotionCache from './utils/createEmotionCache';
import { deleteUnnecessaryCookies } from './utils/helpers';
import { LastPaymentProvider } from './providers/LastPaymentProvider';

//Extend attributes of attributes
//Definition start
type DataAttributeKey = `data-${string}`;
declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    [dataAttribute: DataAttributeKey]: unknown;
  }
}
//Definition end

const clientSideEmotionCache = createEmotionCache();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

deleteUnnecessaryCookies(); // We shouldn't have to do this but we do

const App = () => {
  useEffect(() => {
    initializeAnalytics();
  }, []);

  return (
    <CacheProvider value={clientSideEmotionCache}>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <SnackbarStoreProvider>
            <ReactQueryDevtools
              initialIsOpen={false}
              buttonPosition="bottom-left"
            />
            <PaymentStoreProvider>
              <RecentlyLinkedPolicyProvider>
                <LastPaymentProvider>
                  <CssBaseline />
                  <RouterProvider router={router} />
                </LastPaymentProvider>
              </RecentlyLinkedPolicyProvider>
            </PaymentStoreProvider>
          </SnackbarStoreProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default App;
